// ============================================================================
// review-labels — the operator's own mark on a job (migration 088).
// ----------------------------------------------------------------------------
// The palette and the five calls that read and write it. Kept out of the
// components so the Run Sheet, the picker and the Settings section all agree
// about what a tone IS without importing each other.
//
// FIELD NAMES. `apiFetch` converts top-level snake_case to camelCase, so every
// consumer reads `sortOrder`, `reviewLabelId`, `reviewLabelName`,
// `reviewLabelTone` (CLAUDE.md HARD RULE). Writing the snake_case forms here
// would read `undefined` in production while a raw-fetch harness passed green.
// ============================================================================

import { apiFetch } from './data-service-api.js';

export type ReviewLabelTone = 'slate' | 'blue' | 'violet' | 'teal' | 'rose' | 'amber';

export interface ReviewLabel {
    id: string;
    name: string;
    tone: ReviewLabelTone;
    sortOrder?: number;
    createdAt?: string;
    updatedAt?: string;
    /** How many jobs currently carry this label. Present on the LIST response
     *  only, because it exists for one reason: the delete confirm must name a
     *  real number BEFORE it deletes, and the count the DELETE returns arrives
     *  too late to put in a dialog. */
    usageCount?: number;
}

/**
 * SIX FIXED SLOTS, and no free colour picker.
 *
 * Every pair below is already shipped somewhere in the app, so none adds brand
 * token debt and none needs a fresh contrast run. A picker was rejected for two
 * reasons that are not matters of taste: a tenant's white-label `--qm-accent`
 * can land arbitrarily close to any operator-picked hex (so a label would stop
 * reading as a label), and the obvious amber a human reaches for, #d97706, is
 * 3.1:1 on white and fails AA — a picker hands the operator that failure
 * directly.
 *
 * THERE IS NO GREEN. #15803d on #f0fdf4 already means "milestone done" and
 * "WON" on the very same preview panel, so a green review label sitting beside
 * a green outcome chip would be a lie about what the colour means.
 *
 * `fg` is the text colour, `tint` the fill, `edge` the border. The CSS mirror
 * of this table lives in src/styles/dashboard-runsheet.css as the
 * `.rs-label[data-tone=...]` rules; the two must move together, and the
 * migration's CHECK constraint is the third copy that pins the SLUG set.
 */
export const TONES: Record<ReviewLabelTone, { label: string; fg: string; tint: string; edge: string }> = {
    // ClientsPage neutral, 7.0:1. The default, so an operator who never picks a
    // colour still gets something legible rather than something loud.
    slate: { label: 'Slate', fg: '#4b5563', tint: '#f5f5f4', edge: '#e7e5e4' },
    blue: { label: 'Blue', fg: '#1d4ed8', tint: '#eff6ff', edge: '#c3d9f7' },
    violet: { label: 'Violet', fg: '#7c3aed', tint: '#ede9fe', edge: '#ddd6fe' },
    teal: { label: 'Teal', fg: '#0f766e', tint: '#f0fdfa', edge: '#ccfbf1' },
    // Both of these are lifted verbatim from the outcome chips already on the
    // preview panel, so a label never introduces a second red or a second amber.
    rose: { label: 'Rose', fg: '#991b1b', tint: '#fef2f2', edge: '#e08a8a' },
    amber: { label: 'Amber', fg: '#b45309', tint: '#fffbeb', edge: '#e5c07b' },
};

export const TONE_ORDER: ReviewLabelTone[] = ['slate', 'blue', 'violet', 'teal', 'rose', 'amber'];

/** Anything unrecognised falls back to the neutral slot rather than rendering
 *  an unstyled chip. A tone can only go missing via a hand-edited database or a
 *  future slug, and in both cases a grey label is better than an invisible one. */
export function resolveTone(raw: unknown): ReviewLabelTone {
    const v = String(raw ?? '');
    return (TONE_ORDER as string[]).includes(v) ? (v as ReviewLabelTone) : 'slate';
}

export async function listReviewLabels(): Promise<ReviewLabel[]> {
    const rows = await apiFetch('/review-labels');
    return Array.isArray(rows) ? rows : [];
}

export async function createReviewLabel(
    input: { name: string; tone?: ReviewLabelTone; sortOrder?: number },
): Promise<ReviewLabel> {
    return apiFetch('/review-labels', {
        method: 'POST',
        body: JSON.stringify({
            name: input.name,
            tone: input.tone || 'slate',
            sortOrder: input.sortOrder ?? 0,
        }),
    });
}

export async function updateReviewLabel(
    id: string,
    patch: { name?: string; tone?: ReviewLabelTone; sortOrder?: number },
): Promise<ReviewLabel> {
    return apiFetch(`/review-labels/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
}

export async function deleteReviewLabel(id: string): Promise<{ id: string; clearedProjects: number }> {
    return apiFetch(`/review-labels/${id}`, { method: 'DELETE' });
}

/**
 * Assign or clear the mark on one job. `labelId: null` clears it.
 *
 * Its own narrow route, not the full project PUT: PUT assigns title/address/
 * scope without COALESCE, so a partial payload nulls the envelope.
 */
export async function setProjectReviewLabel(
    projectId: string,
    labelId: string | null,
): Promise<{ id: string; reviewLabelId: string | null }> {
    return apiFetch(`/projects/${projectId}/review-label`, {
        method: 'PATCH',
        body: JSON.stringify({ labelId }),
    });
}
